import {
  Alerts,
  Button,
  Expandable,
  expandableChevronSx,
  Text,
} from '@components/ui';
import CriteriaOption from '@features/r8tr/jobs/criteria-option/criteria-option';
import { queryKeys } from '@lib/api/queryKeys';
import { useRatingCategoriesQuery, useRatingDamagesQuery, useRatingOptionsQuery } from '@lib/api/rating';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import { useQueryClient } from '@tanstack/react-query';
import { type KeyboardEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import type {
  RatingCategoriesResult,
  RatingCriteria,
  RatingDamageGroup,
  RatingLocationState,
  RatingOption,
} from './r8tingTypes';


const sectionSx: SxProps<Theme> = {
  display: 'flex',
  width: { xs: '100%', md: '83.333%', lg: '66.666%' },
  flexDirection: 'row',
  justifyContent: 'flex-start',
};

const criteriaRowSx = (open: boolean): SxProps<Theme> => ({
  fontFamily: '"Plus Jakarta Sans", sans-serif',
  height: 50,
  width: '100%',
  cursor: 'pointer',
  tableLayout: 'fixed',
  borderCollapse: 'collapse',
  pr: '50px',
  display: 'table',
  m: 0,
  bgcolor: open ? 'text.primary' : 'rgb(var(--field))',
  color: open ? 'background.paper' : 'text.secondary',
  border: 1,
  borderColor: open ? 'text.primary' : 'divider',
  '&:focus-visible': {
    outline: '2px solid',
    outlineColor: 'primary.main',
    outlineOffset: 2,
  },
});

const R8ting = (): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [openedCriteriaId, setOpenedCriteriaId] = useState<
    string | number | null
  >(null);
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [criteriaList, setCriteriaList] = useState<RatingCriteria[]>([]);
  const [criteriaListTitle, setCriteriaListTitle] = useState('');
  const location = useLocation();
  const jobData = (location.state as RatingLocationState).jobData;
  const { data: ratingCategoriesResult = {} } = useRatingCategoriesQuery(
    jobData.id
  );
  const ratingCategories = ratingCategoriesResult as RatingCategoriesResult;
  const { data: ratingOptionsResult = [] } =
    useRatingOptionsQuery(selectedOptionId);
  const ratingOptions = ratingOptionsResult as RatingOption[];
  const { data: ratingDamagesResult = [] } = useRatingDamagesQuery(
    openedCriteriaId as string | undefined,
    jobData.id
  );
  const ratingDamages = ratingDamagesResult as RatingDamageGroup[];
  useEffect(() => {
    if (
      ratingCategories.ratingCriterias &&
      ratingCategories.ratingCriterias.length > 0
    ) {
      setCriteriaList(ratingCategories.ratingCriterias);
    }
  }, [ratingCategories]);
  const handleR8tingCategoryListUpdates = (): void => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.ratingCategories(jobData.id),
    });
  };
  const handleR8tingOptionsList = (
    id: string | number,
    optionId: string | number | null | undefined,
    title: string | undefined
  ): void => {
    setCriteriaListTitle(title ?? '');
    if (openedCriteriaId == id) {
      setOpenedCriteriaId(null);
    } else {
      setOpenedCriteriaId(id);
    }
    setSelectedOptionId(
      optionId === undefined || optionId === null ? null : String(optionId)
    );
  };

  const handleContinue = (): void => {
    navigate('/upload-videos', { state: { jobData: jobData } });
  };

  const handlePressNext = (): void => {
    if (
      ratingCategories.ratingsCompleted &&
      ratingCategories.ratingsCompleted == true
    ) {
      Alerts.ratingsCompletedAlert(handleContinue);
    } else {
      toast.error(
        'Please complete ratings for all criterias before proceeding'
      );
    }
  };

  return (
    <Box>
      <Box
        sx={{
          mt: '25px',
          display: 'flex',
          width: '100%',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box sx={sectionSx}>
          <Text
            as="span"
            variant="title"
            sx={{
              width: '100%',
              borderBottom: 1,
              borderColor: 'divider',
              textTransform: 'uppercase',
              fontWeight: 700,
            }}
          >
            {jobData.make} {jobData.name}-{jobData.model}
          </Text>
        </Box>
        <Box sx={sectionSx}>
          <Box
            sx={{
              mt: '15px',
              display: 'flex',
              width: { xs: '100%', sm: '80%' },
              flexDirection: 'row',
              justifyContent: 'space-between',
              '@media (max-width:490px)': {
                alignItems: 'center',
                justifyContent: 'space-evenly',
                textAlign: 'center',
              },
            }}
          >
            <Box
              component="svg"
              viewBox="0 0 100 100"
              version="1.1"
              xmlns={jobData.category.icon}
              sx={{ mr: '10px', width: 40, height: 40, flexShrink: 0 }}
            >
              <defs>
                <pattern
                  id="img"
                  patternUnits="userSpaceOnUse"
                  width="100"
                  height="100"
                >
                  <image
                    xlinkHref={jobData.category.icon}
                    x="-25"
                    width="250"
                    height="100"
                  />
                </pattern>
              </defs>
              <polygon
                points="50 1 95 25 95 75 50 99 5 75 5 25"
                fill="url(#img)"
              />
            </Box>

            <Text sx={{ mb: 0, fontWeight: 700 }}>
              <Box component="span" sx={{ fontSize: 14, fontWeight: 700 }}>
                {t('rating.price')}
              </Box>
              <br />
              <Box
                component="span"
                sx={{ fontSize: 14, fontWeight: 700, color: 'primary.main' }}
              >
                ${jobData.askingPrice}
              </Box>
            </Text>

            <Text sx={{ mb: 0 }}>
              <Box component="span" sx={{ fontSize: 14, fontWeight: 700 }}>
                {t('rating.zipCode')}
              </Box>
              <br />
              <Box
                component="span"
                sx={{ fontSize: 14, fontWeight: 700, color: 'text.secondary' }}
              >
                {jobData.zip}
              </Box>
            </Text>

            <Text sx={{ mb: 0 }}>
              <Box component="span" sx={{ fontSize: 14, fontWeight: 700 }}>
                {t('rating.customer')}
              </Box>
              <br />
              <Box
                component="span"
                sx={{ fontSize: 14, fontWeight: 700, color: 'text.secondary' }}
              >
                {jobData.contactNumber}
              </Box>
            </Text>
          </Box>
        </Box>
        <Box sx={sectionSx}>
          <Box
            sx={{
              mt: 2.5,
              display: 'flex',
              width: '100%',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            {criteriaList.map((item) => {
              const open = openedCriteriaId == item.id;
              return (
                <Box key={item.id} sx={{ width: '100%' }}>
                  <Box
                    component="ul"
                    role="button"
                    tabIndex={0}
                    aria-expanded={open}
                    onClick={() => {
                      handleR8tingOptionsList(
                        item.id,
                        item.optionId,
                        item.title
                      );
                    }}
                    onKeyDown={(event: KeyboardEvent<HTMLElement>) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleR8tingOptionsList(
                          item.id,
                          item.optionId,
                          item.title
                        );
                      }
                    }}
                    sx={criteriaRowSx(open)}
                  >
                    <Box
                      component="li"
                      sx={{
                        display: 'table-cell',
                        fontWeight: 700,
                        wordBreak: 'break-word',
                        verticalAlign: 'middle',
                        textTransform: 'uppercase',
                        color: 'inherit',
                        pl: '10px',
                        fontSize: 14,
                      }}
                    >
                      {item.title}
                    </Box>
                    <Box
                      component="li"
                      sx={{
                        display: 'table-cell',
                        wordBreak: 'break-word',
                        pr: '10px',
                        textAlign: 'end',
                        verticalAlign: 'middle',
                        color: 'inherit',
                      }}
                    >
                      <Box
                        component="p"
                        sx={{
                          display: 'inline',
                          fontSize: 14,
                          fontWeight: 700,
                          mr: '50px',
                          '@media (max-width:490px)': { mr: '40px' },
                        }}
                      >
                        {item.ratingCriteriaUser != null
                          ? `RATED ${item.ratingCriteriaUser.ratingValue}`
                          : 'NOT RATED'}
                      </Box>
                      <KeyboardArrowDownIcon
                        sx={[{ fontSize: 18 }, expandableChevronSx(open)]}
                      />
                    </Box>
                  </Box>
                  <Expandable open={open}>
                    <CriteriaOption
                      ratingCriteriaOptions={ratingOptions}
                      damages={ratingDamages[0]?.damages}
                      ratingCriteria={item}
                      jobId={jobData.id}
                      ratingCriteriaId={item.id}
                      criteriaListTitle={criteriaListTitle}
                      handleR8tingCategoryListUpdates={
                        handleR8tingCategoryListUpdates
                      }
                    />
                  </Expandable>
                </Box>
              );
            })}
          </Box>
        </Box>
        <Box sx={[sectionSx, { my: 2, justifyContent: 'center' }]}>
          <Button
            variant="primary"
            onClick={handlePressNext}
            sx={{
              height: 45,
              width: '25%',
              borderRadius: '0px 15px 0px 15px',
              color: 'common.white',
            }}
          >
            {t('common.next')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default R8ting;
